import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/context/AuthContext";
import { AddContactDialog } from "@/components/crm/AddContactDialog";
import { NewLeadDialog } from "@/components/crm/NewLeadDialog";
import { AddFollowupDialog } from "@/components/followup/AddFollowupDialog";
import { NewTransactionDialog } from "@/components/transaction/NewTransactionDialog";
import { NewTaskDialog } from "@/components/run/NewTaskDialog";
import { NewEventDialog } from "@/components/run/NewEventDialog";
import { UploadDocumentDialog } from "@/components/run/UploadDocumentDialog";
import { GoalDialog } from "@/components/goals/GoalDialog";
import { LicenseDialog } from "@/components/compliance/ComplianceDialogs";
import { emitCrmChanged } from "@/services/crmService";

const CrmActionsContext = createContext(null);

/**
 * App-wide CRM actions: opens the Add Contact / New Lead / Add Follow-Up dialogs
 * from anywhere (Quick Create, CRM, Contact Profile, Pipeline, Smart Follow-Up).
 * Broadcasts crm:changed so open list/board pages refetch after a mutation.
 */
export const CrmActionsProvider = ({ children }) => {
  const navigate = useNavigate();
  const { status, platformRole } = useAuth();
  const isStaff = Boolean(platformRole);
  const showDialogs = status === "authenticated" && !isStaff;
  const [addOpen, setAddOpen] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const [presetContact, setPresetContact] = useState(null);
  const [followupOpen, setFollowupOpen] = useState(false);
  const [followupCtx, setFollowupCtx] = useState({ contact: null, lead: null, leads: [] });
  const [txOpen, setTxOpen] = useState(false);
  const [txCtx, setTxCtx] = useState({ contact: null, lead: null });
  const [taskOpen, setTaskOpen] = useState(false);
  const [taskRelated, setTaskRelated] = useState(null);
  const [eventOpen, setEventOpen] = useState(false);
  const [eventCtx, setEventCtx] = useState({ related: null, start: null });
  const [docOpen, setDocOpen] = useState(false);
  const [docRelated, setDocRelated] = useState(null);
  const [goalOpen, setGoalOpen] = useState(false);
  const [licenseOpen, setLicenseOpen] = useState(false);

  const openAddContact = useCallback(() => setAddOpen(true), []);
  const openNewLead = useCallback((contact = null) => { setPresetContact(contact); setLeadOpen(true); }, []);
  const openAddFollowup = useCallback((contact, lead = null, leads = []) => {
    setFollowupCtx({ contact, lead, leads });
    setFollowupOpen(true);
  }, []);
  const openNewTransaction = useCallback((contact = null, lead = null) => {
    setTxCtx({ contact, lead });
    setTxOpen(true);
  }, []);
  const openNewTask = useCallback((related = null) => { setTaskRelated(related); setTaskOpen(true); }, []);
  const openNewEvent = useCallback((related = null, start = null) => { setEventCtx({ related, start }); setEventOpen(true); }, []);
  const openUploadDocument = useCallback((related = null) => { setDocRelated(related); setDocOpen(true); }, []);
  const openNewGoal = useCallback(() => setGoalOpen(true), []);
  const openAddLicense = useCallback(() => setLicenseOpen(true), []);

  const onContactCreated = useCallback((contact) => {
    setAddOpen(false);
    emitCrmChanged();
    if (contact?.id) navigate(`/crm/${contact.id}`);
  }, [navigate]);

  const onLeadCreated = useCallback(() => {
    setLeadOpen(false);
    setPresetContact(null);
    emitCrmChanged();
    navigate("/pipeline");
  }, [navigate]);

  const value = useMemo(() => ({ openAddContact, openNewLead, openAddFollowup, openNewTransaction, openNewTask, openNewEvent, openUploadDocument, openNewGoal, openAddLicense }), [openAddContact, openNewLead, openAddFollowup, openNewTransaction, openNewTask, openNewEvent, openUploadDocument, openNewGoal, openAddLicense]);

  return (
    <CrmActionsContext.Provider value={value}>
      {children}
      {/* Workspace-less platform staff never create CRM records, and nothing should
          mount before the session resolves — skip these dialogs (and their
          workspace-scoped fetches like /pipeline/stages) until authenticated as a customer. */}
      {showDialogs && (
        <>
          <AddContactDialog open={addOpen} onOpenChange={setAddOpen} onCreated={onContactCreated} onViewExisting={(id) => navigate(`/crm/${id}`)} />
          <NewLeadDialog open={leadOpen} onOpenChange={(v) => { setLeadOpen(v); if (!v) setPresetContact(null); }} presetContact={presetContact} onCreated={onLeadCreated} />
          <AddFollowupDialog
            open={followupOpen}
            onOpenChange={setFollowupOpen}
            contact={followupCtx.contact}
            lead={followupCtx.lead}
            leads={followupCtx.leads}
            onCreated={() => emitCrmChanged()}
          />
          <NewTransactionDialog
            open={txOpen}
            onOpenChange={setTxOpen}
            presetContact={txCtx.contact}
            presetLead={txCtx.lead}
            onCreated={() => emitCrmChanged()}
          />
          <NewTaskDialog open={taskOpen} onOpenChange={setTaskOpen} presetRelated={taskRelated} onCreated={() => emitCrmChanged()} />
          <NewEventDialog open={eventOpen} onOpenChange={setEventOpen} presetRelated={eventCtx.related} presetStart={eventCtx.start} onSaved={() => emitCrmChanged()} />
          <UploadDocumentDialog open={docOpen} onOpenChange={setDocOpen} presetRelated={docRelated} onUploaded={() => emitCrmChanged()} />
          <GoalDialog open={goalOpen} onOpenChange={setGoalOpen} onSaved={() => emitCrmChanged()} />
          <LicenseDialog open={licenseOpen} onOpenChange={setLicenseOpen} onSaved={() => emitCrmChanged()} />
        </>
      )}
    </CrmActionsContext.Provider>
  );
};

export const useCrmActions = () => {
  const ctx = useContext(CrmActionsContext);
  return ctx || { openAddContact: () => {}, openNewLead: () => {}, openAddFollowup: () => {}, openNewTransaction: () => {}, openNewTask: () => {}, openNewEvent: () => {}, openUploadDocument: () => {}, openNewGoal: () => {}, openAddLicense: () => {} };
};
